// @flow

import React, { Component, type Element } from "react";
import { noop, isEqual, map, get, times, debounce } from "lodash";

import type {
  NullaryFn,
  UnaryFn,
  BinaryFn,
  TernaryFn,
  QuadFn
} from "@tvg/types/Functional";
import type {
  RaceCardBettingInterest,
  RaceTypeCodeEnum,
  HandicapOptionsType,
  HandicapSortOptionsType,
  ExpandLineArgs,
  RaceListItem,
  RaceProgram
} from "@tvg/types/Race";
import formatSequencial from "@tvg/formatter/numeric";
import handicappingOptions, {
  handicappingGreyhoundOptions
} from "@tvg/utils/handicapping-conf/handicappingOptions";
import type { HandicappingRunnerFlags } from "@tvg/atomic-ui/_molecule/RunnerFlags";

import Icon from "../../_static/Icons";
import { key, close } from "../../_static/Icons/icons";

import buildColor from "../../_static/ColorPalette";
import type { SelectionType } from "../../_atom/SelectionButton";
import RunnerListHeader from "../../_molecule/RunnerListHeader";
import RunnerList, { type Selections } from "../../_organism/RunnerList";
import RaceInfoInline from "../../_molecule/RaceInfoInline";
import MagnetScrollArea from "../MagnetScrollArea";

import {
  RaceCardSection,
  RaceCardLegWrapper,
  RaceCardTableWrapper,
  RaceCardTable,
  RaceDataContainer,
  Text,
  BetAllModalWrapper,
  BetAllModal,
  CloseButton,
  BetAllButtons,
  BetAllButton
} from "./styled-components";

import type { HandicapSelectiontype } from "../HandicappingList/handicappingOptions";

const getSelectionText = (
  selectionType: SelectionType,
  column: number,
  selected: boolean
) => {
  const text = [];
  switch (selectionType) {
    case "key":
      if (column === 0) {
        text.push(
          <Icon key="key-icon" icon={key} color={buildColor("blue", "800")} />
        );
      } else {
        text.push(<Text key="line1">{!selected ? "W/" : "RESET"}</Text>);
        text.push(<Text key="line2">ALL</Text>);
      }
      break;
    case "number":
      text.push(
        <Text key="line1">
          {!selected ? formatSequencial(column + 1) : "RESET"}
        </Text>
      );
      text.push(<Text key="line2">ALL</Text>);
      break;
    case "default":
    default:
      text.push(<Text key="line1">{!selected ? "BET" : "RESET"}</Text>);
      text.push(<Text key="line2">ALL</Text>);
      break;
  }

  return text;
};
type Props = {
  /**
   * Array of the Race Betting Interests
   */
  bettingInterests: Array<Array<RaceCardBettingInterest>>,
  /**
   * Inline data of this race
   */
  racesData: Array<Array<RaceListItem>>,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * Flag that controls if the race is wagerable or not
   */
  isWagerable: boolean,
  /**
   * Array with the current selections of the RaceCard
   */
  selections: Selections,
  /**
   * Flag to activate a key column
   */
  isKey: boolean,
  /**
   * Number of selection columns
   */
  numColumns: number,
  /**
   * To confirm, that all runners are selected
   */
  isAllSelected: Array<boolean>,
  /**
   * Calback to run when user selects a runner
   */
  onRunnerSelect: BinaryFn<string, number, mixed>,
  /**
   * Callback to run when user selects the select all button
   */
  onAllRunnersSelect: UnaryFn<number, mixed>,
  /**
   * selected leg on pick bets
   */
  selectedLeg: number,
  /**
   * Callback to run when change the selected leg for pick bets
   */
  onLegSelect: UnaryFn<number, void>,
  /**
   * type of handicapping to choose format
   */
  handicapType: HandicapOptionsType,
  /**
   * For the first element of inline handicapping be bold
   */
  firstHandicapBold: boolean,
  /**
   * Should handicapping format be with fixed layout
   */
  fixHandicapSection: {
    isHandicapFixed: boolean,
    fixedOffset: number,
    handicapFormatHeight: number
  },
  /**
   * has handicapping dropdown
   */
  hasHandicappingSelection: boolean,
  /**
   **************** talent pick tool props *******************
   */
  /**
   * Activate favorite selection
   */
  isFavoriteSelectionActive: boolean,
  /**
   * Selected the favorite
   */
  favoriteSelections: string[][],
  /**
   * The race alerts render function
   */
  renderRaceAlerts: NullaryFn<mixed>,
  /**
   * The device this is displaying on
   */
  device: "mobile" | "tablet",
  /**
   * Runner Line expanded by leg
   */
  legExpandedRunner: string[],
  /**
   * Runner Line expand callBack
   */
  expandLine: UnaryFn<ExpandLineArgs, void>,
  /**
   * Render Expanded info
   */
  expandInfo: QuadFn<
    string,
    number,
    RaceCardBettingInterest[],
    string,
    Element<*> | void
  >,
  /**
    should expand for ipp
   */
  inLinePastPerformance: boolean,
  /**
   * Callback subscribe base event
   */
  subscribeBaseEvent: TernaryFn<
    boolean,
    string,
    UnaryFn<{ payload: mixed }, void>,
    void
  >,
  /**
   *  List of Handicap options to show
   */
  handicapOptions: HandicapSelectiontype[],
  /**
   *  Handicapping Sort Mode
   */
  handicappingSortMode: HandicapSortOptionsType,
  /**
   * Track data source
   */
  trackDataSource: string,
  /**
   * Current Race should update racecard
   */
  currentRace: RaceProgram,
  handicappingRunnerFlags: HandicappingRunnerFlags,
  scrollRunnerPP: boolean,
  scrollOffset: number
};

type State = {
  isBetAllOpened: boolean,
  headerHeight: number
};

type HeaderRef = { current: null | HTMLTableSectionElement };

export default class RaceCard extends Component<Props, State> {
  static defaultProps = {
    bettingInterests: [[]],
    racesData: [[]],
    raceTypeCode: "T",
    handicapType: "basic",
    isWagerable: true,
    selections: [[]],
    isAllSelected: [],
    numColumns: 1,
    isKey: false,
    onRunnerSelect: noop,
    onAllRunnersSelect: noop,
    selectedLeg: 0,
    onLegSelect: noop,
    fixHandicapSection: {
      isHandicapFixed: false,
      fixedOffset: 0,
      handicapFormatHeight: 24
    },
    firstHandicapBold: false,
    hasHandicappingSelection: false,
    isFavoriteSelectionActive: false,
    favoriteSelections: [[]],
    renderRaceAlerts: noop,
    device: "mobile",
    legExpandedRunner: [],
    expandLine: noop,
    expandInfo: noop,
    inLinePastPerformance: false,
    subscribeBaseEvent: noop,
    handicapOptions: [],
    handicappingSortMode: "saddlecloth",
    trackDataSource: "",
    currentRace: {},
    handicappingRunnerFlags: {
      title: "",
      subtitle: "",
      subtitleRunnerFlags: "",
      flags: []
    },
    scrollRunnerPP: false,
    scrollOffset: 0
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isBetAllOpened: false,
      headerHeight: 40
    };

    this.refHeader = React.createRef();
  }

  componentDidMount() {
    const headerDimensions = this.refHeader.current;
    if (headerDimensions) {
      this.setHeight(headerDimensions.getBoundingClientRect().height);
    }

    const column = document.getElementById("programpage-dualColumn-right");
    if (column) {
      column.addEventListener("scroll", this.debounceOffset, {
        passive: true
      });
    } else {
      window.addEventListener("scroll", this.debounceOffset, {
        passive: true
      });
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    return (
      nextProps.bettingInterests.length !==
        this.props.bettingInterests.length ||
      nextProps.bettingInterests[0].length !==
        this.props.bettingInterests[0].length ||
      !isEqual(nextProps, this.props)
    );
  }

  componentDidUpdate(nextProps: Props) {
    if (!isEqual(nextProps, this.props)) {
      const headerDimensions = this.refHeader.current;
      // Get Header Height to Bet All
      if (headerDimensions) {
        this.setHeight(headerDimensions.getBoundingClientRect().height);
      }

      // Check change bets type
      if (
        this.props.numColumns !== nextProps.numColumns &&
        this.state.isBetAllOpened
      ) {
        this.toggleBetAll();
      }
    }
  }

  componentWillUnmount() {
    const column = document.getElementById("programpage-dualColumn-right");
    if (column) {
      column.removeEventListener("scroll", this.debounceOffset, {
        passive: true
      });
    } else {
      window.removeEventListener("scroll", this.debounceOffset, {
        passive: true
      });
    }
  }

  getHandicappingFormat = (handicapKey: HandicapOptionsType) => {
    const option =
      this.props.raceTypeCode === "G"
        ? handicappingGreyhoundOptions
        : handicappingOptions;

    const findRes =
      option.find((handicapItem) => handicapItem.key === handicapKey) ||
      option[0];

    return handicapKey === "basic"
      ? {
          title: findRes.title
        }
      : findRes;
  };

  setHeight = (height: number) => {
    if (height > 0) this.setState({ headerHeight: height });
  };

  getOffset = () => {
    const headerDimensions = this.refHeader.current;
    const topToHide = this.props.device === "mobile" ? 48 : 130;
    if (
      headerDimensions &&
      headerDimensions.getBoundingClientRect().top < topToHide &&
      this.state.isBetAllOpened
    ) {
      this.toggleBetAll();
    }
  };

  debounceOffset = debounce(() => this.getOffset(), 150);

  refHeader: HeaderRef;

  handleSelectAll(index: number) {
    this.props.onAllRunnersSelect(index);
  }

  toggleBetAll = () => {
    this.setState({ isBetAllOpened: !this.state.isBetAllOpened });
    this.forceUpdate();
  };

  renderRaceInfo(leg: number) {
    return (
      <RaceDataContainer data-qa-label="raceCard-header-leg">
        <RaceInfoInline raceData={this.props.racesData[leg]} />
      </RaceDataContainer>
    );
  }

  renderRaceCard = () => {
    if (this.props.bettingInterests.length > 1) {
      return (
        <MagnetScrollArea
          onHandleTransition={this.props.onLegSelect}
          activeIndex={this.props.selectedLeg}
          isLoading={false}
          key={`RaceCardWithLeg${this.props.bettingInterests.length}`}
        >
          {map(
            this.props.bettingInterests,
            (bi: RaceCardBettingInterest[], leg: number) => (
              <RaceCardLegWrapper
                data-qa-label="raceCard-leg"
                key={`RaceCard-leg-${leg + 1}`}
              >
                {this.renderRaceInfo(leg)}
                {this.renderLegRaceCard(bi, leg, true)}
              </RaceCardLegWrapper>
            )
          )}
        </MagnetScrollArea>
      );
    }

    return (
      this.props.bettingInterests.length &&
      this.renderLegRaceCard(this.props.bettingInterests[0])
    );
  };

  renderLegRaceCard = (
    bi: RaceCardBettingInterest[],
    leg: number = 0,
    isLeg: boolean = false
  ) => {
    const handicappingFormat = this.getHandicappingFormat(
      this.props.handicapType
    );

    return (
      <RaceCardTableWrapper device={this.props.device} isLeg={isLeg}>
        {this.renderBetAllModal()}
        <RaceCardTable
          data-qa-label="raceCard-table"
          device={this.props.device}
        >
          <RunnerListHeader
            isHandicapFixed={get(
              this.props,
              "fixHandicapSection.isHandicapFixed"
            )}
            fixedHandicapOffset={get(
              this.props,
              "fixHandicapSection.fixedOffset"
            )}
            handicapInfo={get(handicappingFormat, "handicapData")}
            raceTypeCode={this.props.raceTypeCode}
            handicapOptions={this.props.handicapOptions}
            leg={leg}
            isWagerable={this.props.isWagerable}
            isLeg={isLeg}
            isAllSelected={
              isLeg ? [this.props.isAllSelected[leg]] : this.props.isAllSelected
            }
            numColumns={this.props.numColumns}
            isKey={this.props.isKey}
            onAllRunnersSelect={this.props.onAllRunnersSelect}
            hasHandicappingSelection={this.props.hasHandicappingSelection}
            refHeader={this.refHeader}
            toggleBetAll={this.toggleBetAll}
            handicappingSortMode={this.props.handicappingSortMode}
          />
          <RunnerList
            runners={bi}
            leg={leg}
            isLeg={isLeg}
            selections={
              isLeg ? [this.props.selections[leg]] : this.props.selections
            }
            isWagerable={this.props.isWagerable}
            onRunnerSelect={this.props.onRunnerSelect}
            raceTypeCode={this.props.raceTypeCode}
            numColumns={this.props.numColumns}
            isKey={this.props.isKey}
            firstHandicapBold={this.props.firstHandicapBold}
            isFavoriteSelectionActive={this.props.isFavoriteSelectionActive}
            favoriteSelections={this.props.favoriteSelections}
            expandedRunner={this.props.legExpandedRunner[leg] || ""}
            expandLine={this.props.expandLine}
            expandInfo={this.props.expandInfo}
            inLinePastPerformance={this.props.inLinePastPerformance}
            handicappingSortMode={this.props.handicappingSortMode}
            trackDataSource={this.props.trackDataSource}
            currentRace={this.props.currentRace}
            handicappingRunnerFlags={this.props.handicappingRunnerFlags}
            scrollRunnerPP={this.props.scrollRunnerPP}
            scrollOffset={this.props.scrollOffset}
          />
        </RaceCardTable>
      </RaceCardTableWrapper>
    );
  };

  renderSelectAll = () => {
    const selectionType = (() => {
      if (!this.props.isKey) {
        return this.props.numColumns === 1 ? "default" : "number";
      }

      return "key";
    })();

    const buildQALabel = (column) => {
      if (this.props.isKey) {
        return column === 0
          ? "runnerListHeader-selectKeyButton"
          : "runnerListHeader-selectWithButton";
      }

      return this.props.numColumns === 1
        ? "runnerListHeader-selectAllButton"
        : `runnerListHeader-select-${column + 1}-button`;
    };

    return times(this.props.numColumns, (column) => (
      <BetAllButton
        key={`button-${column}`}
        onClick={() => this.handleSelectAll(column)}
        qaLabel={buildQALabel(column)}
        isBetAllOpened={this.state.isBetAllOpened}
        column={column + 1}
        numColumns={this.props.numColumns}
      >
        {getSelectionText(
          selectionType,
          column,
          this.props.isAllSelected[column]
        )}
      </BetAllButton>
    ));
  };

  renderBetAllModal = () => (
    <BetAllModalWrapper
      isBetAllOpened={this.state.isBetAllOpened}
      numColumns={this.props.numColumns}
      data-qa-label="raceCard-betAllSelector"
    >
      <BetAllModal
        headerHeight={this.state.headerHeight}
        device={this.props.device}
      >
        <CloseButton
          onClick={this.toggleBetAll}
          isBetAllOpened={this.state.isBetAllOpened}
          numColumns={this.props.numColumns}
          data-qa-label="raceCard-betAllSelector-closeBtn"
        >
          <Icon icon={close} size={16} />
        </CloseButton>
        <BetAllButtons>{this.renderSelectAll()}</BetAllButtons>
      </BetAllModal>
    </BetAllModalWrapper>
  );

  render() {
    return (
      <RaceCardSection
        device={this.props.device}
        data-qa-label="raceCard-section"
        isHandicapFixed={get(this.props, "fixHandicapSection.isHandicapFixed")}
        handicapFormatHeight={get(
          this.props,
          "fixHandicapSection.handicapFormatHeight"
        )}
        biLength={this.props.bettingInterests.length}
      >
        {this.renderRaceCard()}
        {this.props.renderRaceAlerts()}
      </RaceCardSection>
    );
  }
}
